import { validatedDisplayView } from "../../i18n/display-view.js";
import { portalText } from "../../i18n/portal.js";
/**
 * Area `campaign` of the console gateway — owned by plan-002 unit **RB-25** (design §15.6.5).
 *
 * Campaigns (P14-02's screens), over the six routes RB-25 added. The register held one row,
 * `replaceCampaignQuestionSet` (deferred to plan-003, DD-14), until the product owner withdrew replacing a
 * campaign's questions (2026-10-09, answer 40: a campaign asks what its agent asks); the row and the method are gone.
 *
 * ── WHETHER ANYBODY IS BEING RUNG, STATED PLAINLY (plan-008) ──────────────────────────────
 * AMENDED 2026-09-30 (plan-008 ST-UA): the campaign dialler rings people through the account's own
 * SIP trunk (ST-WK), so `dialling_state` is one of `dialling`, `paused`, `outside_window`,
 * `no_route`, `switched_off` and `finished` (design §22.13.2; `switched_off` is ST-AN's sixth, the
 * account's campaign dialling off). The two stand-in values (`waiting_for_dialler`, `not_dialling`)
 * are gone from the API and are refused here like any other unknown word. `etaText` says when a
 * campaign is not dialling and never shows an estimated finish for one that rings nobody.
 *
 * The detail also carries the campaign's caller-ID number (`caller_id_phone_number_id`,
 * `caller_id_e164`) and what that number is worth as a route through the trunk (`sip_trunk_route`),
 * so the screen can say WHY a campaign cannot dial. `createCampaign` sends the caller-ID number; the
 * API refuses a draft without one (`422 caller_id_not_bound`). A skipped person's `skip_reason` may be
 * one of the runtime's refusal words the dialler records (IC-73, {@link CAMPAIGN_DIAL_REFUSAL}),
 * beside AC-31's reasons.
 *
 * ── THE MAPPING ──────────────────────────────────────────────────────────────────────────
 * Every `…Text` field is worded here from facts (design §15.5.2): `startedText`/`etaText` from
 * the timestamps and the dialling state, `windowText` with `callingHoursText` (2026-10-30), `timeZoneNote`
 * is the recipient-zone sentence (the hours are each person's own), `retryText` from attempts and
 * the wait, `paceText` from the measured pace, the estimate's `assumption` from the three figures
 * the API returns beside the band (they are provisional estimates, and the sentence says so), and
 * `concurrencyNote` from the draft's lines and the tier ceiling. Outcome labels are console copy.
 *
 * ── THE AGENT'S OWN QUESTIONNAIRE, END AND DELETE (2026-10-04) ─────────────────────────────
 * A survey VoiceAgent asks the questionnaire it was built with, so a campaign for one takes no question
 * list: `getVoiceAgentQuestionnaire` reads the questions for New campaign (`asksQuestionnaire`), the
 * draft leaves `question` out when it has none, and every campaign carries `questionSource`,
 * `voiceAgentQuestion` (text per language, kind, choices) and `deletable`. `endCampaign` stops a campaign
 * for good (`cancelled` on the wire, "Ended" on screen); `deleteCampaign` removes one that has called
 * nobody and is refused `409 campaign_not_deletable` otherwise.
 *
 * ── EACH PERSON'S OWN START AND END; EDIT BEFORE START (plan-015 CMP, G16, G17) ───────────────
 * A campaign starts at `start_local_date` `start_local_time` and stops at the end of `end_local_date` on each
 * person's own clock. `createCampaign` sends the three (it no longer sends `starts_at`, which the API computes);
 * the detail reads them back with `editable`, `contactListId`, the attempts and the wait, so New campaign can open
 * as an edit; `updateCampaign` is `PATCH /v1/campaigns/{campaign_id}`. AMENDED 2026-10-30 (owner): a started campaign
 * stays editable until it finishes, and `lockedField` names what it keeps (its contact list and its start; `409
 * campaign_field_locked` on a change to one, `409 campaign_finished` once it has finished or was ended).
 * They were optional on the gateway until the sandbox carried them (plan-015 INT-A, 2026-10-09: `outbound-sandbox.ts`
 * keeps the facts and the edit); both gateways now answer them, so they are required. A skipped person may read
 * `campaign_ended`.
 *
 * ── NO CURSOR ON `listCampaigns(id)`, SO THE SLICE FOLLOWS `next_cursor` ───────────────────
 * Design §15.5.3: follow it until `null`, and stop with an error after {@link PAGE_CEILING} pages
 * rather than truncate.
 */
import { ApiError, assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";

import type {
  ConsoleGateway,
  ReturningPersonTreatmentView,
  CampaignDraftSuggestionView,
  CampaignDraftView,
  CampaignEstimateView,
  CampaignOutcomeShareView,
  CampaignQuestionDraftView,
  CampaignQuestionKindView,
  CampaignRowView,
  CampaignSkipReasonView,
  CampaignSkipRowView,
  CampaignStateView,
  CampaignView,
} from "../gateway.js";
import { money } from "@wetalk/domain";

import { readMoney, readNullableText, readTalkRateKind } from "../conversation-view.js";
import { MalformedResponseError, readArray, readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import {
  CAMPAIGN_OUTCOME_CODE,
  RECIPIENT_TIME_ZONE_NOTE,
  VOICEMAIL_DETECTION_MODE_VIEW,
  callingHoursText,
  concurrencyNote,
  percentText,
  type CallingRangeView,
} from "../outbound-view.js";
import { type GatewayGap, type SliceContext } from "./slice-context.js";

/** A whole number, or `null` where the API states "none" (campaign fix-up 2026-10-28: the agent's line limit). */
function readNullableWholeNumber(value: unknown, path: string): number | null {
  return value === null ? null : readWholeNumber(value, path);
}

// ── plan-008: dialling through the account's own SIP trunk ─────────────────────────────────

/** `dialling_state` (design §22.13.2, with ST-AN's `switched_off`). */
export type CampaignDiallingStateView =
  | "dialling"
  | "paused"
  | "outside_window"
  | "no_route"
  | "switched_off"
  | "finished";

export const CAMPAIGN_DIALLING_STATE: readonly CampaignDiallingStateView[] = [
  "dialling",
  "paused",
  "outside_window",
  "no_route",
  "switched_off",
  "finished",
];

/** `sip_trunk_route`: what the campaign's caller-ID number is worth as a route through the trunk. */
export type CampaignSipTrunkRouteView =
  | "ready"
  | "no_caller_id"
  | "caller_id_not_bound"
  | "sip_trunk_unverified"
  | "sip_trunk_disabled";

export const CAMPAIGN_SIP_TRUNK_ROUTE: readonly CampaignSipTrunkRouteView[] = [
  "ready",
  "no_caller_id",
  "caller_id_not_bound",
  "sip_trunk_unverified",
  "sip_trunk_disabled",
];

/**
 * The runtime's refusal words (IC-73) the campaign dialler records as a skipped attempt's
 * `skip_reason` when nothing was dialled (ST-WK's `CAMPAIGN_DIAL_REFUSAL`, design §22.11).
 */
export type CampaignDialRefusalView =
  | "number_not_bound"
  | "destination_refused"
  | "sip_trunk_unverified"
  | "voice_agent_paused"
  | "voice_agent_unavailable"
  | "model_resting"
  | "draining"
  | "sip_edge_unavailable"
  /** plan-015 SIP (G13): the caller-ID number's trunk only receives calls. */
  | "sip_trunk_inbound_only"
  /**
   * plan-015 QSET (G19): the version a campaign pinned is not this VoiceAgent's ready version. Since QSET-OFF (owner
   * answer 40) no campaign pins one, so this is only read back from a skip recorded before.
   */
  | "agent_version_not_eligible";

export const CAMPAIGN_DIAL_REFUSAL: readonly CampaignDialRefusalView[] = [
  "number_not_bound",
  "destination_refused",
  "sip_trunk_unverified",
  "voice_agent_paused",
  "voice_agent_unavailable",
  "model_resting",
  "draining",
  "sip_edge_unavailable",
  "sip_trunk_inbound_only",
  "agent_version_not_eligible",
];

/**
 * plan-015 CMP (G16): the skip recorded once for a person whose own local end passed while attempts remained.
 */
export type CampaignEndedSkipView = "campaign_ended";

/** Why a person was not rung: AC-31's reasons, the end date, or a refusal the dialler met. */
export type CampaignSkipWordView = CampaignSkipReasonView | CampaignEndedSkipView | CampaignDialRefusalView;

/** A campaign in the list, with whether anybody is being rung. */
export interface CampaignListRowView extends CampaignRowView {
  readonly diallingState: CampaignDiallingStateView;
  /**
   * WAPP-DASH (2026-10-28): the summary's own instants, carried so the dashboard's Campaigns card can tell a
   * campaign starting or ending within a day — `starts_at` (when it is due to start) and `estimated_finish_at`
   * (projected from a running campaign's measured pace; `null` until it has dialled somebody).
   */
  readonly startsAt: string | null;
  readonly estimatedFinishAt: string | null;
}

/** What a question of the agent's questionnaire is (2026-10-04): a statement on the scale, or a choice. */
/** ADR 0053: `statement` for a scale question, `choice`, `open`, `number`, and v1's `emotion` / `source`. */
export type VoiceAgentQuestionKindView = "statement" | "emotion" | "source" | "choice" | "open" | "number";

/** One question of the questionnaire the agent's live version asks, as the Results tab reads it. */
export interface VoiceAgentQuestionView {
  readonly questionCode: string;
  readonly instrumentCode: string;
  readonly questionKind: VoiceAgentQuestionKindView;
  /** The arm a statement belongs to; `null` for the emotion and source questions. */
  readonly variant: "A" | "B" | null;
  readonly ordinal: number;
  /** Language code → the question in that language. */
  readonly text: Readonly<Record<string, string>>;
  /** The choices of an emotion or source question, in the questionnaire's order; empty for a statement. */
  readonly option: readonly { readonly answer: string; readonly label: Readonly<Record<string, string>> }[];
  /** How many choices one person may give; `null` for a statement. */
  readonly selectionMax: number | null;
}

/** What a campaign for this agent asks: its own questionnaire, or (`asksQuestionnaire` false) a typed list. */
export interface VoiceAgentQuestionnaireView {
  readonly voiceAgentId: string;
  readonly agentVersionId: string | null;
  readonly asksQuestionnaire: boolean;
  readonly question: readonly VoiceAgentQuestionView[];
}

/** Where a campaign's questions come from: the agent's questionnaire, or the ones typed at creation. */
export type CampaignQuestionSourceView = "voice_agent_questionnaire" | "campaign_question";

/** One campaign, with its dialling state and the caller-ID number it dials from. */
export interface CampaignDetailView extends CampaignView {
  readonly diallingState: CampaignDiallingStateView;
  /** `null` for a campaign created before plan-008 (it cannot dial until one is set). */
  readonly callerIdPhoneNumberId: string | null;
  /** That number in E.164 while it is a trunk number of this workspace; otherwise `null`. */
  readonly callerIdE164: string | null;
  readonly sipTrunkRoute: CampaignSipTrunkRouteView;
  readonly questionSource: CampaignQuestionSourceView;
  /** The questions the agent's live version asks; empty when it carries no questionnaire. */
  readonly voiceAgentQuestion: readonly VoiceAgentQuestionView[];
  /** Whether it has called nobody, so it may be deleted; otherwise it can only be ended. */
  readonly deletable: boolean;
  // ── plan-015 CMP (G16, G17); required since INT-A (2026-10-09), when the sandbox gateway carries them too. ──
  /** The contact list it calls. */
  readonly contactListId: string;
  readonly returningPerson: ReturningPersonTreatmentView;
  readonly attemptsPerPerson: number;
  readonly waitBetweenAttemptMinute: number;
  /** `YYYY-MM-DD` / `HH:MM` in each person's own zone; `null` only on a campaign that started "as soon as possible". */
  readonly startLocalDate: string | null;
  readonly startLocalTime: string | null;
  /** The last calling date in each person's own zone, or `null` for no end. */
  readonly endLocalDate: string | null;
  /** Whether `updateCampaign` may still change it: until it finishes or is ended (2026-10-30). */
  readonly editable: boolean;
  /** What a started campaign keeps — its contact list and its start; empty before the start. */
  readonly lockedField: readonly CampaignLockedFieldView[];
}

/** 2026-10-30: the fields a campaign keeps once it has started, as the API names them. */
export const CAMPAIGN_LOCKED_FIELD = ["contact_list_id", "start_local_date", "start_local_time"] as const;
export type CampaignLockedFieldView = (typeof CAMPAIGN_LOCKED_FIELD)[number];

/** plan-015 CMP: the start and end a create sends, read on each person's own clock. */
export interface CampaignLocalStartView {
  /** `YYYY-MM-DD`. */
  readonly startLocalDate: string;
  /** `HH:MM`. */
  readonly startLocalTime: string;
  /** `YYYY-MM-DD`, or `null` for no end. */
  readonly endLocalDate: string | null;
}

/** plan-015 CMP (G17): what `updateCampaign` sends — every field a campaign may change before it starts. */
export interface CampaignEditView extends CampaignLocalStartView {
  readonly name: string;
  readonly contactListId: string;
  /** 2026-10-30: the calling ranges, sent as `calling_hours` (replacing whatever the campaign had). */
  readonly callingHours: readonly CallingRangeView[];
  readonly attemptsPerPerson: number;
  readonly waitBetweenAttemptMinute: number;
  readonly simultaneousCall: number;
  /**
   * 2026-10-30: set on a campaign that has started — its contact list and its start are left out of the `PATCH`, so
   * it keeps them (a legacy campaign with no stored start included); the three fields above are then not sent.
   */
  readonly keepsListAndStart?: true;
}

/**
 * The draft `createCampaign` sends. plan-015 CMP: the start and end are required by the API too (`400` on
 * `start_local_date` without them) and New campaign always sends them; they are optional here only so drafts
 * written before them (an estimate's, a fixture's) keep their shape. The caller-ID number is required by the
 * API — a draft without one is refused `422 caller_id_not_bound` — and the form never sends one without it; it
 * is optional here only so an estimate's draft (which the API reads without it) is the same shape.
 */
export interface CampaignCreateDraftView extends CampaignDraftView, Partial<CampaignLocalStartView> {
  readonly callerIdPhoneNumberId?: string;
}

export interface CampaignSkipRowDetailView extends Omit<CampaignSkipRowView, "skipReason"> {
  readonly skipReason: CampaignSkipWordView;
}

/** AC-31's page, with the dialler's refusal words beside AC-31's reasons. */
export interface CampaignSkipReportView {
  readonly row: readonly CampaignSkipRowDetailView[];
  readonly nextCursor: string | null;
  readonly totalCount: number;
  /** Every one of AC-31's reasons (zero included); a refusal word only when somebody was skipped for it. */
  readonly countByReason: Readonly<Record<CampaignSkipReasonView, number>> &
    Readonly<Partial<Record<CampaignEndedSkipView | CampaignDialRefusalView, number>>>;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    listCampaigns(voiceAgentId: string): Promise<readonly CampaignListRowView[]>;
    getCampaign(campaignId: string): Promise<CampaignDetailView>;
    /** A band with its assumption, because an unrun campaign has no measured answer rate. */
    estimateCampaign(draft: CampaignDraftView): Promise<CampaignEstimateView>;
    /** Refused `422 caller_id_not_bound` without a caller-ID number on a verified trunk bound to the agent. */
    createCampaign(draft: CampaignCreateDraftView): Promise<CampaignDetailView>;
    /**
     * What New campaign opens with for this agent, or `null` to open it empty. The API
     * proposes nothing — a real account's form starts empty — so only the sandbox answers, with
     * the design's worked example ("September customers · 1,840 people").
     */
    suggestCampaignDraft(voiceAgentId: string): Promise<CampaignDraftSuggestionView | null>;
    /** Resuming re-checks the trunk route (the same `422` refusals as create); pausing is never refused. */
    setCampaignPaused(campaignId: string, paused: boolean): Promise<CampaignDetailView>;
    /** The questions a campaign for this agent asks (2026-10-04). */
    getVoiceAgentQuestionnaire(voiceAgentId: string): Promise<VoiceAgentQuestionnaireView>;
    /** Stops the campaign for good; a conversation in progress runs to its end. */
    endCampaign(campaignId: string): Promise<CampaignDetailView>;
    /** Removes a campaign that has called nobody; `409 campaign_not_deletable` otherwise. */
    deleteCampaign(campaignId: string): Promise<void>;
    /**
     * plan-015 CMP (G17): `PATCH /v1/campaigns/{campaign_id}` — change a campaign before it starts; `409
     * campaign_started` once it has.
     */
    readonly updateCampaign: (campaignId: string, edit: CampaignEditView) => Promise<CampaignDetailView>;
    /** AC-31: the skipped rows, each with its reason (or the refusal the dialler met). */
    listCampaignSkips(campaignId: string, cursor: string | null): Promise<CampaignSkipReportView>;
  }
}

/**
 * This area's rows of `GATEWAY_GAP`: none. DD-14's `replaceCampaignQuestionSet` (deferred to plan-003) was withdrawn
 * by owner decision 2026-10-09 (answer 40) — a campaign's questions change only with its agent.
 */
export const campaignGap: readonly GatewayGap[] = [];

/** Design §15.5.3: a cursorless console method follows at most this many pages, then raises. */
export const PAGE_CEILING = 20;

/** The API's maximum page, so the ceiling is as far off as it can be. */
const PAGE_LIMIT = 100;

/** Raised when a VoiceAgent has more than {@link PAGE_CEILING} pages of campaigns. Never a silent truncation. */
export class CampaignPageCeilingError extends Error {
  override readonly name = "CampaignPageCeilingError";
  constructor() {
    super(
      portalText("shell", "listcampaigns_followed_value_pages_of_value_and_the_api_st_1f33d402", { v0: String(String(PAGE_CEILING)), v1: String(String(PAGE_LIMIT)) }),
    );
  }
}

/**
 * `422 campaign_simultaneous_call_over_workspace_limit` with the workspace's own line limit
 * (`error.detail.workspace_channel_unit_limit`; campaign fix-up 2026-10-28). The console has no other read of that limit,
 * so the new-campaign screen stops its slider at it and asks again. Still an `ApiError` under its code, so every
 * refusal reader words it as before.
 */
export class CampaignWorkspaceLimitError extends ApiError {
  readonly workspaceChannelUnitLimit: number;
  constructor(result: ApiResult, workspaceChannelUnitLimit: number) {
    super(result.status, result.body, result.requestId);
    this.workspaceChannelUnitLimit = workspaceChannelUnitLimit;
  }
}

/** The answer, or a {@link CampaignWorkspaceLimitError} when it is the workspace-limit refusal; anything else as `assertOk`. */
function assertOkWithWorkspaceLimit(result: ApiResult, operation: string): unknown {
  if (result.status === 422) {
    const error = (result.body as { error?: { code?: unknown; detail?: { workspace_channel_unit_limit?: unknown } } } | null)?.error;
    if (error?.code === "campaign_simultaneous_call_over_workspace_limit") {
      const limit = error.detail?.workspace_channel_unit_limit;
      if (typeof limit !== "number" || !Number.isInteger(limit) || limit < 0) {
        throw new MalformedResponseError(`${operation}.error.detail.workspace_channel_unit_limit`, "a whole number from 0", limit);
      }
      throw new CampaignWorkspaceLimitError(result, limit);
    }
  }
  return assertOk(result);
}

/** What `etaText` says for a due campaign that rings nobody, per dialling state. */
export const NOT_DIALLING_TEXT: Readonly<Record<"no_route" | "switched_off" | "outside_window", string>> = validatedDisplayView({
  get no_route() { return portalText("shell", "not_dialling_it_has_no_sip_trunk_route_0bef824e"); },
  get switched_off() { return portalText("shell", "not_dialling_campaign_dialling_is_switched_off_for_this_ac_53ad83be"); },
  get outside_window() { return portalText("shell", "not_dialling_yet_ea68fad9"); },
});

const STATE: readonly CampaignStateView[] = ["draft", "scheduled", "running", "paused", "finished", "cancelled"];
const QUESTION_SOURCE: readonly CampaignQuestionSourceView[] = ["voice_agent_questionnaire", "campaign_question"];
const VOICE_AGENT_QUESTION_KIND: readonly VoiceAgentQuestionKindView[] = ["statement", "emotion", "source", "choice", "open", "number"];
const KIND: readonly CampaignQuestionKindView[] = ["scale", "choice", "open"];
const SKIP_REASON: readonly CampaignSkipReasonView[] = [
  "suppressed",
  "already_surveyed",
  "called_within_window",
  "no_answer_limit",
  "outside_local_calling_hours",
  "unknown_time_zone",
  "invalid_number",
];
const SKIP_WORD: readonly CampaignSkipWordView[] = [...SKIP_REASON, "campaign_ended", ...CAMPAIGN_DIAL_REFUSAL];
const RETURNING_PERSON: readonly ReturningPersonTreatmentView[] = ["same_questions", "different_questions", "skip_them"];

/**
 * How each outcome reads on the Campaign screen. Console copy, the sandbox's wording. Keyed by the code, so a new
 * outcome is one entry here (the record type makes a missing one a type error). plan-031 VMD-WAPP: `voicemail`.
 */
const OUTCOME_LABEL: Readonly<Record<CampaignOutcomeShareView["code"], string>> = validatedDisplayView({
  get completed() { return portalText("shell", "completed_1798b3ba"); },
  // plan-033 WAPP-HUP (design §44.10): the two kinds of hang-up. AMENDED 2026-10-29 (decoder tightened, §44.12): the
  // api has reported only these two since plan-033 went live (2026-10-28), so a `partial` outcome row is not read.
  get hung_up_mid_way() { return portalText("shell", "campaign_outcome.hung_up_mid_way"); },
  get hung_up_before_first_question() { return portalText("shell", "campaign_outcome.hung_up_before_first_question"); },
  // plan-033 (the truthful labels), AMENDED by OD-23 (2026-10-28): a busy line or a rejected ring now settles
  // `no_answer` and is retried, so `no_answer` reads "No answer, busy or rejected"; `declined` is only ever a busy leg
  // settled before that change, which was not called again — "Busy or rejected, not called again" (design §44.16).
  get declined() { return portalText("shell", "campaign_outcome.declined"); },
  get no_answer() { return portalText("shell", "campaign_outcome.no_answer"); },
  get voicemail() { return portalText("shell", "voicemail.outcome"); },
});

/** How one outcome code reads (the sandbox draws its bars with the same words). */
export function campaignOutcomeLabelOf(code: CampaignOutcomeShareView["code"]): string {
  return OUTCOME_LABEL[code];
}

/** The one assumption the API's band is computed under (`estimate.ts` in the campaign area). */
const KNOWN_ASSUMPTION = "answer_rate_band_at_average_length";

function readOneOf<T extends string>(value: unknown, path: string, allowed: readonly T[], wanted: string): T {
  const raw = readText(value, path);
  const found = allowed.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, wanted, raw);
  return found;
}

/**
 * `Today 14:02` reads as `today 14:02` mid-sentence; a date stays as it is. WAPP-DASH (2026-10-28): `Tomorrow`, and
 * the Greek day words (`Σήμερα`, `Χτες`, `Αύριο`), are lowered too — «Ξεκινά σήμερα 12:00», not «Ξεκινά Σήμερα 12:00».
 */
function midSentence(whenText: string): string {
  return whenText.replace(/^(Today|Yesterday|Tomorrow|Σήμερα|Χτες|Αύριο)/u, (word) =>
    word.toLocaleLowerCase(/^[A-Z]/.test(word) ? "en" : "el"),
  );
}

function plural(count: number, one: string, many: string): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}

/** "30 minutes", "4 hours", "1 day", "90 minutes". */
function waitText(minute: number): string {
  if (minute > 0 && minute % 1440 === 0) return plural(minute / 1440, "day", "days");
  if (minute > 0 && minute % 60 === 0) return plural(minute / 60, "hour", "hours");
  return plural(minute, "minute", "minutes");
}

/** "2 minutes 24 seconds". */
function durationText(second: number): string {
  const minute = Math.floor(second / 60);
  const rest = second % 60;
  if (minute === 0) return plural(rest, "second", "seconds");
  if (rest === 0) return plural(minute, "minute", "minutes");
  return `${plural(minute, "minute", "minutes")} ${plural(rest, "second", "seconds")}`;
}

export function retryTextOf(attemptsPerPerson: number, waitMinute: number): string {
  const retry = attemptsPerPerson - 1;
  if (retry <= 0) return portalText("shell", "no_retries_92a75e66");
  if (retry === 1) return portalText("shell", "1_retry_value_later_1018bc45", { v0: String(waitText(waitMinute)) });
  return portalText("shell", "value_retries_value_apart_c1889d02", { v0: String(String(retry)), v1: String(waitText(waitMinute)) });
}

interface SummaryFact {
  readonly state: CampaignStateView;
  readonly diallingState: CampaignDiallingStateView;
  readonly startsAt: string | null;
  readonly startedAt: string | null;
  readonly finishedAt: string | null;
  readonly estimatedFinishAt: string | null;
}

function summaryFactOf(row: Readonly<Record<string, unknown>>, path: string): SummaryFact {
  return {
    state: readOneOf(row["state"], `${path}.state`, STATE, portalText("shell", "a_campaign_state_661289a4")),
    diallingState: readOneOf(row["dialling_state"], `${path}.dialling_state`, CAMPAIGN_DIALLING_STATE, portalText("shell", "a_dialling_state_d8a50f11")),
    startsAt: readNullableText(row["starts_at"], `${path}.starts_at`),
    startedAt: readNullableText(row["started_at"], `${path}.started_at`),
    finishedAt: readNullableText(row["finished_at"], `${path}.finished_at`),
    estimatedFinishAt: readNullableText(row["estimated_finish_at"], `${path}.estimated_finish_at`),
  };
}

function startedTextOf(fact: SummaryFact, now: Date, context: SliceContext): string {
  if (fact.startedAt !== null) return portalText("shell", "started_value_927101e7", { v0: String(midSentence(context.formatWhen(fact.startedAt, now))) });
  if (fact.startsAt !== null) return startsTextOf(fact.startsAt, now, context.formatWhen);
  return portalText("shell", "starts_as_soon_as_it_can_dda0c279");
}

function etaTextOf(fact: SummaryFact, now: Date, context: SliceContext): string {
  // `cancelled` is a campaign a member ended (2026-10-04); `finished_at` is when.
  if (fact.state === "cancelled") {
    return fact.finishedAt === null ? portalText("shell", "ended_90303d8d") : portalText("shell", "ended_value_3b68365b", { v0: String(midSentence(context.formatWhen(fact.finishedAt, now))) });
  }
  if (fact.finishedAt !== null) return portalText("shell", "finished_value_fa0e3887", { v0: String(midSentence(context.formatWhen(fact.finishedAt, now))) });
  if (fact.state === "paused" || fact.diallingState === "paused") return portalText("shell", "paused_c7dfb6f1");
  if (fact.diallingState === "no_route" || fact.diallingState === "switched_off") {
    return NOT_DIALLING_TEXT[fact.diallingState];
  }
  if (fact.diallingState === "outside_window") return NOT_DIALLING_TEXT.outside_window;
  if (fact.estimatedFinishAt !== null) {
    return portalText("shell", "estimated_to_finish_value_459fe128", { v0: String(midSentence(context.formatWhen(fact.estimatedFinishAt, now))) });
  }
  return portalText("shell", "no_estimate_yet_3a22266a");
}

/**
 * WAPP-DASH (2026-10-28): "Starts today 12:00" — the words `startedText` uses for a campaign not yet started, for the
 * dashboard's Campaigns card, which words its rows against its own clock.
 */
export function startsTextOf(startsAt: string, now: Date, formatWhen: SliceContext["formatWhen"]): string {
  return portalText("shell", "starts_value_77a0eee5", { v0: String(midSentence(formatWhen(startsAt, now))) });
}

/** WAPP-DASH (2026-10-28): "Ends tomorrow 14:00" — a running campaign's estimated finish, as the dashboard states it. */
export function endsTextOf(estimatedFinishAt: string, now: Date, formatWhen: SliceContext["formatWhen"]): string {
  return portalText("shell", "campaign_ends_value", { v0: midSentence(formatWhen(estimatedFinishAt, now)) });
}

/** One `CampaignSummary` as the campaign list renders it. */
export function campaignRowOf(wire: unknown, path: string, now: Date, context: SliceContext): CampaignListRowView {
  const row = readRecord(wire, path);
  const fact = summaryFactOf(row, path);
  return validatedDisplayView({
    campaignId: readText(row["campaign_id"], `${path}.campaign_id`),
    name: readText(row["name"], `${path}.name`),
    state: fact.state,
    contactListName: readText(row["contact_list_name"], `${path}.contact_list_name`),
    personCount: readWholeNumber(row["person_count"], `${path}.person_count`),
    calledCount: readWholeNumber(row["called_count"], `${path}.called_count`),
    completedCount: readWholeNumber(row["completed_count"], `${path}.completed_count`),
    get startedText() { return startedTextOf(fact, now, context); },
    get etaText() { return etaTextOf(fact, now, context); },
    diallingState: fact.diallingState,
    startsAt: fact.startsAt,
    estimatedFinishAt: fact.estimatedFinishAt,
  });
}

/** `[{ language_code, text }]` as language → text. */
function languageTextOf(wire: unknown, path: string): Readonly<Record<string, string>> {
  const out: Record<string, string> = {};
  readArray(wire, path).forEach((one, index) => {
    const entry = readRecord(one, `${path}[${String(index)}]`);
    out[readText(entry["language_code"], `${path}[${index}].language_code`)] = readText(
      entry["text"],
      `${path}[${index}].text`,
    );
  });
  return out;
}

/** One `VoiceAgentQuestion` (2026-10-04). */
export function voiceAgentQuestionOf(wire: unknown, path: string): VoiceAgentQuestionView {
  const row = readRecord(wire, path);
  const variant = row["variant"];
  const selectionMax = row["selection_max"];
  return {
    questionCode: readText(row["question_code"], `${path}.question_code`),
    instrumentCode: readText(row["instrument_code"], `${path}.instrument_code`),
    questionKind: readOneOf(row["question_kind"], `${path}.question_kind`, VOICE_AGENT_QUESTION_KIND, portalText("shell", "a_questionnaire_question_kind_308aac5f")),
    variant: variant === null ? null : readOneOf(variant, `${path}.variant`, ["A", "B"] as const, portalText("shell", "an_arm_a3ac21a3")),
    ordinal: readWholeNumber(row["ordinal"], `${path}.ordinal`),
    text: languageTextOf(row["text"], `${path}.text`),
    option: readArray(row["option"], `${path}.option`).map((one, index) => {
      const at = `${path}.option[${index}]`;
      const entry = readRecord(one, at);
      return validatedDisplayView({ answer: readText(entry["answer"], `${at}.answer`), get label() { return languageTextOf(entry["label"], `${at}.label`); } });
    }),
    selectionMax: selectionMax === null ? null : readWholeNumber(selectionMax, `${path}.selection_max`),
  };
}

/** "14 questions", counted as a person hears them: one arm's statements plus the choice questions. */
export function voiceAgentQuestionCountText(question: readonly VoiceAgentQuestionView[]): string {
  const arms = new Set(question.map((one) => one.variant).filter((one) => one !== null));
  const shared = question.filter((one) => one.variant === null).length;
  if (arms.size <= 1) return plural(question.length, "question", "questions");
  const perArm = [...arms].map((arm) => question.filter((one) => one.variant === arm).length + shared);
  return portalText("shell", "value_arms_value_questions_24b1bb83", { v0: String(String(arms.size)), v1: String(perArm.map((count) => String(count)).join(" and ")) });
}

/** One `Campaign` as the Campaign screen renders it. */
export function campaignViewOf(wire: unknown, path: string, now: Date, context: SliceContext): CampaignDetailView {
  const row = readRecord(wire, path);
  const summary = campaignRowOf(row, path, now, context);
  const question = readArray(row["question"], `${path}.question`).map((one, index) => {
    const at = `${path}.question[${index}]`;
    const entry = readRecord(one, at);
    return {
      campaignQuestionId: readText(entry["campaign_question_id"], `${at}.campaign_question_id`),
      ordinal: readWholeNumber(entry["ordinal"], `${at}.ordinal`),
      // Carried, not dropped (register 0z.4): the screen shows the returning set on its own.
      questionSet: readOneOf(entry["question_set"], `${at}.question_set`, ["primary", "returning"], portalText("shell", "a_question_set_0fb4dd64")),
      text: readText(entry["text"], `${at}.text`),
      kind: readOneOf(entry["kind"], `${at}.kind`, KIND, portalText("shell", "a_question_kind_91601f22")),
      answeredCount: readWholeNumber(entry["answered_count"], `${at}.answered_count`),
    };
  });
  const primaryCount = question.filter((one) => one.questionSet === "primary").length;
  const returningCount = question.length - primaryCount;
  const questionSource = readOneOf(row["question_source"], `${path}.question_source`, QUESTION_SOURCE, portalText("shell", "a_question_source_82fc7a9e"));
  const voiceAgentQuestion = readArray(row["voice_agent_question"], `${path}.voice_agent_question`).map((one, index) =>
    voiceAgentQuestionOf(one, `${path}.voice_agent_question[${index}]`),
  );

  const schedule = readRecord(row["schedule"], `${path}.schedule`);
  const dayOfWeek = readArray(schedule["day_of_week"], `${path}.schedule.day_of_week`).map((one, index) =>
    readWholeNumber(one, `${path}.schedule.day_of_week[${index}]`),
  );
  const from = readNullableText(schedule["call_from_local_time"], `${path}.schedule.call_from_local_time`);
  const to = readNullableText(schedule["call_to_local_time"], `${path}.schedule.call_to_local_time`);
  // 2026-10-30: required — the api answers it from the release that stores calling ranges (api before the web-app).
  const callingHours = readArray(schedule["calling_hours"], `${path}.schedule.calling_hours`).map((one, index) => {
    const at = `${path}.schedule.calling_hours[${index}]`;
    const range = readRecord(one, at);
    return {
      dayOfWeek: readWholeNumber(range["day_of_week"], `${at}.day_of_week`),
      fromLocalTime: readText(range["from_local_time"], `${at}.from_local_time`),
      toLocalTime: readText(range["to_local_time"], `${at}.to_local_time`),
    };
  });
  const pace = schedule["pace_per_minute"];

  const cost = readRecord(row["cost"], `${path}.cost`);
  const perCompleted = cost["per_completed"];

  return validatedDisplayView({
    campaignId: summary.campaignId,
    voiceAgentId: readText(row["voice_agent_id"], `${path}.voice_agent_id`),
    diallingState: summary.diallingState,
    callerIdPhoneNumberId: readNullableText(row["caller_id_phone_number_id"], `${path}.caller_id_phone_number_id`),
    callerIdE164: readNullableText(row["caller_id_e164"], `${path}.caller_id_e164`),
    sipTrunkRoute: readOneOf(row["sip_trunk_route"], `${path}.sip_trunk_route`, CAMPAIGN_SIP_TRUNK_ROUTE, portalText("shell", "a_sip_trunk_route_337d5fdc")),
    name: summary.name,
    state: summary.state,
    contactListName: summary.contactListName,
    startedText: summary.startedText,
    etaText: summary.etaText,
    personCount: summary.personCount,
    calledCount: summary.calledCount,
    completedCount: summary.completedCount,
    connectedCount: readWholeNumber(row["connected_count"], `${path}.connected_count`),
    // plan-031 VMD-WAPP (design §42.10, §42.11): both required since VMD-API.
    voicemailCount: readWholeNumber(row["voicemail_count"], `${path}.voicemail_count`),
    // plan-033 WAPP-RET: required since 2026-10-29 (decoder tightened, design §44.12) — the api has sent it since
    // plan-033 went live (2026-10-28), so an absent count is a malformed answer, never "not reported yet".
    calledBackCount: readWholeNumber(row["called_back_count"], `${path}.called_back_count`),
    voicemailDetection: readOneOf(
      row["voicemail_detection"],
      `${path}.voicemail_detection`,
      VOICEMAIL_DETECTION_MODE_VIEW,
      portalText("shell", "voicemail.a_detection_mode"),
    ),
    get questionSetText() { return questionSource === "voice_agent_questionnaire"
        ? portalText("shell", "value_built_into_the_agent_c1bab06b", { v0: String(voiceAgentQuestionCountText(voiceAgentQuestion)) })
        : plural(primaryCount, "question", "questions") +
          (returningCount === 0 ? "" : portalText("shell", "value_for_people_already_surveyed_13da6164", { v0: String(String(returningCount)) })); },
    question,
    questionSource,
    voiceAgentQuestion,
    deletable: readBoolean(row["deletable"], `${path}.deletable`),
    // plan-015 CMP (G16, G17).
    contactListId: readText(row["contact_list_id"], `${path}.contact_list_id`),
    returningPerson: readOneOf(row["returning_person"], `${path}.returning_person`, RETURNING_PERSON, portalText("shell", "a_returning_person_treatment_00bebd0c")),
    attemptsPerPerson: readWholeNumber(schedule["attempts_per_person"], `${path}.schedule.attempts_per_person`),
    waitBetweenAttemptMinute: readWholeNumber(
      schedule["wait_between_attempt_minute"],
      `${path}.schedule.wait_between_attempt_minute`,
    ),
    startLocalDate: readNullableText(row["start_local_date"], `${path}.start_local_date`),
    startLocalTime: readNullableText(row["start_local_time"], `${path}.start_local_time`),
    endLocalDate: readNullableText(row["end_local_date"], `${path}.end_local_date`),
    editable: readBoolean(row["editable"], `${path}.editable`),
    // 2026-10-30: required — the api names it on every campaign body from the release that lets a started one change.
    lockedField: readArray(row["locked_field"], `${path}.locked_field`).map((one, index) =>
      readOneOf(one, `${path}.locked_field[${index}]`, CAMPAIGN_LOCKED_FIELD, portalText("shell", "a_campaign_field_e2b05d97")),
    ),
    outcome: readArray(row["outcome"], `${path}.outcome`).map((one, index) => {
      const at = `${path}.outcome[${index}]`;
      const entry = readRecord(one, at);
      const code = readOneOf(entry["code"], `${at}.code`, CAMPAIGN_OUTCOME_CODE, portalText("shell", "an_outcome_code_fbd656c3"));
      return {
        code,
        label: OUTCOME_LABEL[code],
        count: readWholeNumber(entry["count"], `${at}.count`),
        shareBasisPoint: readWholeNumber(entry["share_basis_point"], `${at}.share_basis_point`),
      };
    }),
    schedule: validatedDisplayView({
      callingHours,
      dayOfWeek,
      callFromLocalTime: from ?? "",
      callToLocalTime: to ?? "",
      get windowText() { return callingHours.length === 0 ? portalText("shell", "no_calling_hours_set_a6d27590") : callingHoursText(callingHours); },
      // The hours are each person's own; a screen must never print them beside one zone.
      get timeZoneNote() { return RECIPIENT_TIME_ZONE_NOTE(); },
      simultaneousCall: readWholeNumber(schedule["simultaneous_call"], `${path}.schedule.simultaneous_call`),
      simultaneousCallCeiling: readWholeNumber(
        schedule["simultaneous_call_ceiling"],
        `${path}.schedule.simultaneous_call_ceiling`,
      ),
      entitlementCeiling: readWholeNumber(schedule["entitlement_ceiling"], `${path}.schedule.entitlement_ceiling`),
      voiceAgentChannelUnitLimit: readNullableWholeNumber(
        schedule["voice_agent_channel_unit_limit"],
        `${path}.schedule.voice_agent_channel_unit_limit`,
      ),
      workspaceChannelUnitLimit: readNullableWholeNumber(
        schedule["workspace_channel_unit_limit"],
        `${path}.schedule.workspace_channel_unit_limit`,
      ),
      get retryText() { return retryTextOf(
        readWholeNumber(schedule["attempts_per_person"], `${path}.schedule.attempts_per_person`),
        readWholeNumber(schedule["wait_between_attempt_minute"], `${path}.schedule.wait_between_attempt_minute`),
      ); },
      get paceText() { return pace === null ? "—" : portalText("shell", "value_calls_a_minute_d2899a48", { v0: String(String(readWholeNumber(pace, `${path}.schedule.pace_per_minute`))) }); },
    }),
    cost: {
      spent: readMoney(cost["spent"], `${path}.cost.spent`),
      talkSecond: readWholeNumber(cost["billed_second"], `${path}.cost.billed_second`),
      perCompleted: perCompleted === null ? null : readMoney(perCompleted, `${path}.cost.per_completed`),
      projected: readMoney(cost["projected"], `${path}.cost.projected`),
      // plan-017 READ-B: the outbound rate of the route it dials on, and what `spent` left out (required fields).
      talkRateKind: readTalkRateKind(cost["talk_rate_kind"], `${path}.cost.talk_rate_kind`),
      talkRatePerMinute: money(
        readWholeNumber(cost["talk_rate_minor_per_minute"], `${path}.cost.talk_rate_minor_per_minute`),
        readMoney(cost["spent"], `${path}.cost.spent`).currency,
      ),
      priceUnavailableCount: readWholeNumber(cost["price_unavailable_count"], `${path}.cost.price_unavailable_count`),
    },
    surveyRoundId: readNullableText(row["survey_round_id"], `${path}.survey_round_id`),
  });
}

function questionWire(one: CampaignQuestionDraftView): Record<string, unknown> {
  return { ordinal: one.ordinal, code: one.code, text: one.text, kind: one.kind };
}

/**
 * The view's draft as the API's `CampaignDraft`. The hours travel with no zone: they are the person's
 * own. The caller-ID number travels when the draft has one (an estimate's draft has none; the API
 * ignores it there). plan-015 CMP: so do the start and end — each person's own — and `starts_at` never does
 * (the API computes it); an estimate's draft carries no start.
 */
function draftWire(draft: CampaignDraftView & Partial<CampaignCreateDraftView>): Record<string, unknown> {
  return {
    ...(draft.startLocalDate === undefined || draft.startLocalTime === undefined
      ? {}
      : {
          start_local_date: draft.startLocalDate,
          start_local_time: draft.startLocalTime,
          end_local_date: draft.endLocalDate ?? null,
        }),
    ...(draft.callerIdPhoneNumberId === undefined ? {} : { caller_id_phone_number_id: draft.callerIdPhoneNumberId }),
    voice_agent_id: draft.voiceAgentId,
    contact_list_id: draft.contactListId,
    name: draft.name,
    // A survey agent asks its own questionnaire and takes no list (2026-10-04): an empty one is left out.
    ...(draft.question.length === 0 ? {} : { question: draft.question.map(questionWire) }),
    returning_person: draft.returningPerson,
    returning_question: draft.returningQuestion === null ? null : draft.returningQuestion.map(questionWire),
    attempts_per_person: draft.attemptsPerPerson,
    wait_between_attempt_minute: draft.waitBetweenAttemptMinute,
    calling_hours: callingHoursWire(draft.callingHours),
    simultaneous_call: draft.simultaneousCall,
  };
}

/** 2026-10-30: the calling ranges as the API's `CampaignCallingHours`. */
function callingHoursWire(hours: readonly CallingRangeView[]): readonly Record<string, unknown>[] {
  return hours.map((one) => ({ day_of_week: one.dayOfWeek, from_local_time: one.fromLocalTime, to_local_time: one.toLocalTime }));
}

/** plan-015 CMP: the edit as the API's `CampaignEditRequest` — every editable field; equal ones change nothing. */
function editWire(edit: CampaignEditView): Record<string, unknown> {
  const kept =
    edit.keepsListAndStart === true
      ? {}
      : { contact_list_id: edit.contactListId, start_local_date: edit.startLocalDate, start_local_time: edit.startLocalTime };
  return {
    name: edit.name,
    ...kept,
    calling_hours: callingHoursWire(edit.callingHours),
    attempts_per_person: edit.attemptsPerPerson,
    wait_between_attempt_minute: edit.waitBetweenAttemptMinute,
    simultaneous_call: edit.simultaneousCall,
    end_local_date: edit.endLocalDate,
  };
}

export function campaignApiSlice(
  client: WeTalkApiClient,
  context: SliceContext,
): Pick<
  ConsoleGateway,
  | "listCampaigns"
  | "getCampaign"
  | "estimateCampaign"
  | "createCampaign"
  | "suggestCampaignDraft"
  | "setCampaignPaused"
  | "getVoiceAgentQuestionnaire"
  | "endCampaign"
  | "deleteCampaign"
  | "listCampaignSkips"
  | "updateCampaign"
> {
  const dataOf = (body: unknown, operationId: string): unknown => readRecord(body, operationId)["data"];

  return {
    async listCampaigns(voiceAgentId: string): Promise<readonly CampaignListRowView[]> {
      const now = context.now();
      const row: CampaignListRowView[] = [];
      let cursor: string | null = null;
      for (let page = 0; page < PAGE_CEILING; page += 1) {
        const query: { limit: number; cursor?: string } =
          cursor === null ? { limit: PAGE_LIMIT } : { limit: PAGE_LIMIT, cursor };
        const body = readRecord(
          assertOk(await client.listCampaigns({ path: { voice_agent_id: voiceAgentId }, query })),
          "listCampaigns",
        );
        for (const one of readArray(body["data"], "listCampaigns.data")) {
          row.push(campaignRowOf(one, `campaign[${row.length}]`, now, context));
        }
        const next = body["next_cursor"];
        if (next === null) return row;
        cursor = readText(next, "listCampaigns.next_cursor");
      }
      throw new CampaignPageCeilingError();
    },

    async getCampaign(campaignId: string): Promise<CampaignDetailView> {
      const body = assertOk(await client.getCampaign({ path: { campaign_id: campaignId } }));
      return campaignViewOf(dataOf(body, "getCampaign"), "getCampaign.data", context.now(), context);
    },

    async estimateCampaign(draft: CampaignDraftView): Promise<CampaignEstimateView> {
      const data = readRecord(
        dataOf(assertOkWithWorkspaceLimit(await client.estimateCampaign({ body: draftWire(draft) }), "estimateCampaign"), "estimateCampaign"),
        "estimateCampaign.data",
      );
      const assumption = readText(data["assumption_code"], "estimateCampaign.assumption_code");
      if (assumption !== KNOWN_ASSUMPTION) {
        throw new MalformedResponseError("estimateCampaign.assumption_code", KNOWN_ASSUMPTION, assumption);
      }
      const low = readWholeNumber(data["answer_rate_low_basis_point"], "estimateCampaign.answer_rate_low_basis_point");
      const high = readWholeNumber(data["answer_rate_high_basis_point"], "estimateCampaign.answer_rate_high_basis_point");
      const second = readWholeNumber(data["average_conversation_second"], "estimateCampaign.average_conversation_second");
      const ceiling = readWholeNumber(data["simultaneous_call_ceiling"], "estimateCampaign.simultaneous_call_ceiling");
      const entitlementCeiling = readWholeNumber(data["entitlement_ceiling"], "estimateCampaign.entitlement_ceiling");
      return {
        personCount: readWholeNumber(data["person_count"], "estimateCampaign.person_count"),
        noTimeZoneCount: readWholeNumber(data["no_time_zone_count"], "estimateCampaign.no_time_zone_count"),
        dayCount: readWholeNumber(data["day_count"], "estimateCampaign.day_count"),
        low: readMoney(data["low"], "estimateCampaign.low"),
        high: readMoney(data["high"], "estimateCampaign.high"),
        get assumption() { return portalText("shell", "assumes_value_value_of_people_answer_and_value_each_nothin_1f1bb5f6", { v0: String(percentText(low)), v1: String(percentText(high)), v2: String(durationText(second)) }); },
        simultaneousCallCeiling: ceiling,
        entitlementCeiling,
        voiceAgentChannelUnitLimit: readNullableWholeNumber(
          data["voice_agent_channel_unit_limit"],
          "estimateCampaign.voice_agent_channel_unit_limit",
        ),
        workspaceChannelUnitLimit: readNullableWholeNumber(
          data["workspace_channel_unit_limit"],
          "estimateCampaign.workspace_channel_unit_limit",
        ),
        // The note speaks of the ORGANIZATION's lines (shared by every agent), so it is worded against the entitlement.
        concurrencyNote: concurrencyNote(draft.simultaneousCall, entitlementCeiling),
        // plan-017 READ-B: the outbound rate of the route named by the draft's caller-ID number (required fields).
        talkRateKind: readTalkRateKind(data["talk_rate_kind"], "estimateCampaign.talk_rate_kind"),
        talkRatePerMinute: money(
          readWholeNumber(data["talk_rate_minor_per_minute"], "estimateCampaign.talk_rate_minor_per_minute"),
          readMoney(data["low"], "estimateCampaign.low").currency,
        ),
        // plan-031 VMD-WAPP: the switch, so the form's voicemail and retry sentences never claim a detection that is off.
        voicemailDetection: readOneOf(
          data["voicemail_detection"],
          "estimateCampaign.voicemail_detection",
          VOICEMAIL_DETECTION_MODE_VIEW,
          portalText("shell", "voicemail.a_detection_mode"),
        ),
      };
    },

    async createCampaign(draft: CampaignCreateDraftView): Promise<CampaignDetailView> {
      const body = assertOk(await client.createCampaign({ body: draftWire(draft) }));
      return campaignViewOf(dataOf(body, "createCampaign"), "createCampaign.data", context.now(), context);
    },

    // No route, and none wanted: the API proposes nothing, so a real New campaign starts empty.
    suggestCampaignDraft: () => Promise.resolve(null),

    async setCampaignPaused(campaignId: string, paused: boolean): Promise<CampaignDetailView> {
      const body = assertOk(
        await client.setCampaignPaused({ path: { campaign_id: campaignId }, body: { paused } }),
      );
      return campaignViewOf(dataOf(body, "setCampaignPaused"), "setCampaignPaused.data", context.now(), context);
    },

    async getVoiceAgentQuestionnaire(voiceAgentId: string): Promise<VoiceAgentQuestionnaireView> {
      const data = readRecord(
        dataOf(
          assertOk(await client.getVoiceAgentQuestionnaire({ path: { voice_agent_id: voiceAgentId } })),
          "getVoiceAgentQuestionnaire",
        ),
        "getVoiceAgentQuestionnaire.data",
      );
      const agentVersionId = data["agent_version_id"];
      return {
        voiceAgentId: readText(data["voice_agent_id"], "getVoiceAgentQuestionnaire.voice_agent_id"),
        agentVersionId: agentVersionId === null ? null : readText(agentVersionId, "getVoiceAgentQuestionnaire.agent_version_id"),
        asksQuestionnaire: readBoolean(data["asks_questionnaire"], "getVoiceAgentQuestionnaire.asks_questionnaire"),
        question: readArray(data["question"], "getVoiceAgentQuestionnaire.question").map((one, index) =>
          voiceAgentQuestionOf(one, `getVoiceAgentQuestionnaire.question[${String(index)}]`),
        ),
      };
    },

    async endCampaign(campaignId: string): Promise<CampaignDetailView> {
      const body = assertOk(await client.endCampaign({ path: { campaign_id: campaignId } }));
      return campaignViewOf(dataOf(body, "endCampaign"), "endCampaign.data", context.now(), context);
    },

    async deleteCampaign(campaignId: string): Promise<void> {
      assertOk(await client.deleteCampaign({ path: { campaign_id: campaignId } }));
    },

    async updateCampaign(campaignId: string, edit: CampaignEditView): Promise<CampaignDetailView> {
      const body = assertOk(await client.updateCampaign({ path: { campaign_id: campaignId }, body: editWire(edit) }));
      return campaignViewOf(dataOf(body, "updateCampaign"), "updateCampaign.data", context.now(), context);
    },

    async listCampaignSkips(campaignId: string, cursor: string | null): Promise<CampaignSkipReportView> {
      const now = context.now();
      const query: { limit: number; cursor?: string } =
        cursor === null ? { limit: PAGE_LIMIT } : { limit: PAGE_LIMIT, cursor };
      const body = readRecord(
        assertOk(await client.listCampaignSkips({ path: { campaign_id: campaignId }, query })),
        "listCampaignSkips",
      );
      const byReason = readRecord(body["count_by_reason"], "listCampaignSkips.count_by_reason");
      const countByReason = {} as Record<CampaignSkipReasonView, number> &
        Partial<Record<CampaignEndedSkipView | CampaignDialRefusalView, number>>;
      for (const reason of SKIP_REASON) {
        countByReason[reason] = readWholeNumber(byReason[reason], `listCampaignSkips.count_by_reason.${reason}`);
      }
      // plan-015 CMP: the end date's skip, counted by the API since 0124 (absent from an older answer).
      if (byReason["campaign_ended"] !== undefined) {
        countByReason.campaign_ended = readWholeNumber(byReason["campaign_ended"], "listCampaignSkips.count_by_reason.campaign_ended");
      }
      // The API counts every word it holds; a refusal word appears only once somebody was skipped for it.
      for (const refusal of CAMPAIGN_DIAL_REFUSAL) {
        if (byReason[refusal] !== undefined) {
          countByReason[refusal] = readWholeNumber(byReason[refusal], `listCampaignSkips.count_by_reason.${refusal}`);
        }
      }
      const next = body["next_cursor"];
      return {
        row: readArray(body["data"], "listCampaignSkips.data").map((one, index) => {
          const at = `listCampaignSkips.data[${String(index)}]`;
          const entry = readRecord(one, at);
          return validatedDisplayView({
            campaignAttemptId: readText(entry["campaign_attempt_id"], `${at}.campaign_attempt_id`),
            e164: readText(entry["e164"], `${at}.e164`),
            displayName: readNullableText(entry["display_name"], `${at}.display_name`),
            skipReason: readOneOf(entry["skip_reason"], `${at}.skip_reason`, SKIP_WORD, portalText("shell", "a_skip_reason_ae964839")),
            get whenText() { return context.formatWhen(readText(entry["attempted_at"], `${at}.attempted_at`), now); },
          });
        }),
        nextCursor: next === null ? null : readText(next, "listCampaignSkips.next_cursor"),
        totalCount: readWholeNumber(body["total_count"], "listCampaignSkips.total_count"),
        countByReason,
      };
    },
  };
}
